import { useAtomCallback } from 'jotai/utils';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { IProviderFieldsDataModel } from '../../data-models/provider-fields.data-model';
import { putProviderFields } from '../queries/MappingsQueries';
import { fieldsMappingByCompanyState } from '../state/CompanyProvidersState';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { ConflictErrorStatus, getErrorMessage, getErrorStatusCode } from '../queryHelpers';
import { duplicateErrorState } from '../../pages/Mappings/state/MappingsUIState';
import { MappingsEventType } from '../../pages/Mappings/MappingsGrid/mappingsGridEvents';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import { getForesightStore } from '../../util/jotai-store';
import { companyStateJ } from '../state/CompanyStateJ';
import { lastFieldUpdateDateStateJ } from '../../pages/Mappings/state/MappingsStateJ';
import { ForesightSupportEmail } from '../../constants/foresight';
import { trackEvent } from '../../util/datadogTracking';

export interface IUpdateProviderPayload extends Partial<IProviderFieldsDataModel<unknown>> {
  id: number;
  companyId: number;
  retainHistory?: boolean;
}

export function useUpdateMappings() {
  const { pushErrorToast } = useToastMessageState();
  const setLastFieldUpdateDate = useSetAtom(lastFieldUpdateDateStateJ);

  return useAtomCallback(
    useCallback(
      async (
        get,
        set,
        updatedFieldData: IUpdateProviderPayload,
        _actionType: MappingsEventType,
        params?: AxiosRequestConfig
      ) => {
        const payload = {
          ...updatedFieldData,
          id: Math.abs(updatedFieldData.id),
          primary: updatedFieldData.primary || null,
        };

        // The event reflects the outcome of the save (putProviderFields) itself.
        // `saved` guards against a post-save cache-update failure being reported
        // as a failed save.
        let saved = false;
        try {
          const newData = await putProviderFields(payload, params);
          saved = true;
          trackEvent('mapping.saved', { success: true });

          const current = await get(fieldsMappingByCompanyState(newData.companyId));
          set(
            fieldsMappingByCompanyState(newData.companyId),
            current?.map((fieldData) => (fieldData.id === newData.id ? newData : fieldData)) ?? []
          );
          setLastFieldUpdateDate(new Date().toISOString());
          return newData;
        } catch (err) {
          if (!saved) {
            trackEvent('mapping.saved', { success: false });
          }
          if (isConflictError(err)) {
            console.error(err);
            const url = getConflictingWebsite(err as AxiosError);
            pushErrorToast({
              message: `Website ${url ? url + ' ' : ''}is linked to another company.
              Please contact support to make this change.`,
              buttonAction: () => window.open(`mailto:${ForesightSupportEmail}`),
              buttonLabel: 'Contact Support',
            });
            return null;
          }
          const message = getErrorMessage(err, 'Error updating mapping');
          pushErrorToast({ message });
          return null;
        }
      },
      [pushErrorToast, setLastFieldUpdateDate]
    )
  );
}

/** @returns true if the error was handled, false otherwise  */
export function useHandleConflicts() {
  const setDuplicateError = useSetAtom(duplicateErrorState);
  return useCallback(
    (error: unknown, editedCompanyId: number, actionType: MappingsEventType) => {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const { data } = error.response;

        if (isDuplicateErrorResponseData(data)) {
          getForesightStore().set(companyStateJ(data.company.id), data.company);
          setDuplicateError({
            id: editedCompanyId,
            intoId: data.company.id,
            key: data.key,
            value: data.value,
            action: actionType,
          });
          return true;
        }
      }

      return false;
    },
    [setDuplicateError]
  );
}

interface DuplicateErrorResponseData {
  company: ICompanyDataModel;
  key: 'primary' | 'secondary';
  value: string;
}
interface ITempDuplicateErrorResponse {
  message: DuplicateErrorResponseData & { error: string };
  statusCode: 409;
}

function isDuplicateErrorResponseData(error: unknown): error is DuplicateErrorResponseData {
  return error instanceof Object && 'company' in error && 'key' in error && 'value' in error;
}

function isConflictError(error: unknown): error is AxiosError {
  return error instanceof AxiosError && getErrorStatusCode(error) === ConflictErrorStatus;
}

function getConflictingWebsite(error?: AxiosError): string | undefined {
  return (error?.response?.data as ITempDuplicateErrorResponse)?.message?.value;
}
